import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { closeModal } from '../../../redux/slices/modalSlice'
import { RootState } from '../../../redux/store'
import { useAppSelector } from '../../../redux/hooks'
import { BRAND_KEY } from '../../../core/const'
import Utils from '../../../core/Utils'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
	ArrowRightIcon,
	BankIcon,
	CheckCircleIcon,
	ChevronDownIcon,
	CloseIcon,
	GiftIcon,
	MastercardMark,
	VisaMark,
	WalletIcon,
	WiseMark,
} from '../../../img/svgComponents/UiIcons'
import { ModalCssAnimation } from '../ModalManager'
import { sports } from '../../../pages/SportPage/sports'
import { Smartico } from '../../../core/Smartico'
import { API } from '../../../core/API'

enum EventName {
	demo_fx_open_position = 'demo_fx_open_position',
	demo_fx_close_position = 'demo_fx_close_position',
	demo_complete_kyc = 'demo_complete_kyc',
	demo_send_documents = 'demo_send_documents',
	client_action = 'client_action',
	demo_fx_margin_call = 'demo_fx_margin_call',
	demo_fx_liquidation = 'demo_fx_liquidation',
	betb_risk_update = 'betb_risk_update',

	casino_bet_win = 'casino_bet_win',
	acc_deposit_approved = 'acc_deposit_approved',
	sport_bet_open = 'sport_bet_open',
	sport_bet_settled = 'sport_bet_settled',

	sport_bet_selection_open = 'sport_bet_selection_open',
	sport_bet_selection_settled = 'sport_bet_selection_settled',
}

interface ISportGame {
	id: number
	home: string
	away: string
	league?: string
}

interface ISportType {
	sportType: string
	games: ISportGame[]
}

const sportsConfig: ISportType[] = [
	{
		sportType: 'Football',
		games: [
			{ id: 11, home: 'Croatia', away: 'England', league: 'EURO 2024' },
			{ id: 12, home: 'Italy', away: 'Serbia', league: 'EURO 2024' },
			{ id: 13, home: 'Juventus', away: 'Liverpool', league: 'UEFA Champions League' },
		],
	},
	{
		sportType: 'Tennis',
		games: [
			{ id: 21, home: 'Riccardo Bonadio', away: 'Joao Domingues' },
			{ id: 22, home: 'Daniel Evans', away: 'Dominik Koepfer' },
			{ id: 23, home: 'Wynnum SC', away: 'Brisbane FC' },
		],
	},
	{
		sportType: 'Baseball',
		games: [
			{ id: 31, home: 'LA Dodgers', away: 'Houston Astros' },
			{ id: 32, home: 'San Francisco Giants', away: 'Chiicago White Sox' },
		],
	},
	{
		sportType: 'Chess',
		games: [{ id: 41, home: 'Magnus Carlsen', away: 'Teimour Radjabov' }],
	},
]

export interface EventData {
	game_name: string
	bet: number
	win: number
	amount: number
	quantity: number
	price: number
	symbol: string
	direction: 'BUY' | 'SELL'
	pnl: number
	action?: string
	sport: string | undefined
	sport_game_id: number
	selection: string
	sport_bet_amount: number
	sport_win_amount: number
	sport_bet_status: boolean
	odds: number
	o_sport_type?: string
	o_sport_game?: ISportGame
	home_team?: string
	away_team?: string
	league?: string
	win_amount?: number
	betb_average_session_time_perc_increase: number
	betb_frequency_of_play_perc_increase: number
	betb_exhibited_risk_level: string
	betb_frequency_risk_level: string
	betb_overall_risk_level: string
	betb_predictive_risk_level: string
}

export const eventInitialData: EventData = {
	game_name: 'Gold Quest',
	bet: 10,
	win: 20,
	amount: 50,
	quantity: 10,
	price: 55.8,
	symbol: 'AAPL',
	direction: 'BUY',
	pnl: 10.25,
	sport: sports[0].sport_id,
	sport_bet_amount: 10,
	sport_win_amount: 15,
	sport_bet_status: true,
	o_sport_type: sports[0].sport_id,
	sport_game_id: sportsConfig[0].games[0].id,
	o_sport_game: sportsConfig[0].games[0],
	home_team: sportsConfig[0].games[0].home,
	away_team: sportsConfig[0].games[0].away,
	selection: sportsConfig[0].games[0].home,
	league: sportsConfig[0].games[0].league,
	odds: 2,
	betb_average_session_time_perc_increase: 5,
	betb_frequency_of_play_perc_increase: 5,
	betb_exhibited_risk_level: 'NOT_RATED',
	betb_frequency_risk_level: 'NOT_RATED',
	betb_overall_risk_level: 'NOT_RATED',
	betb_predictive_risk_level: 'NOT_RATED',
}

export const cleanEvent = (eventName: EventName, eventData: EventData): Partial<any> => {
	switch (eventName) {
		case EventName.client_action: {
			return {
				action: eventData.action,
			}
		}
		case EventName.casino_bet_win: {
			return {
				casino_last_bet_id: Math.random().toString(36).substring(7),
				casino_last_bet_dt: Date.now(),
				casino_last_bet_game_name: eventData.game_name,
				casino_last_bet_amount: eventData.bet,
				casino_last_win_amount: eventData.win,
			}
		}

		case EventName.demo_fx_margin_call:
		case EventName.demo_fx_liquidation:
		case EventName.demo_send_documents:
		case EventName.demo_complete_kyc: {
			return {}
		}
		case EventName.acc_deposit_approved: {
			return {
				acc_last_deposit_date: Date.now(),
				acc_last_deposit_amount: eventData.amount,
				acc_last_transaction_id: Math.random().toString(36).substring(7),
			}
		}
		case EventName.demo_fx_open_position: {
			return {
				quantity: eventData.quantity,
				price: eventData.price,
				symbol: eventData.symbol,
				direction: eventData.direction,
			}
		}
		case EventName.demo_fx_close_position: {
			return {
				quantity: eventData.quantity,
				price: eventData.price,
				symbol: eventData.symbol,
				pnl: eventData.pnl,
			}
		}
		case EventName.sport_bet_open: {
			return {
				sport_last_bet_id: Math.random().toString(36).substring(7),

				sport_last_bet_amount: eventData.sport_bet_amount,

				sport_last_bet_date: Date.now(),
				sport_last_bet_odds: eventData.odds,

				sport_last_bet_timing: 'LIVE',
				sport_last_bet_type: 'SINGLE',
				sport_last_bet_virtual: false,
				sport_last_bet_sports: [eventData.sport],
				sport_last_bet_leagues: [eventData.league],
				sport_last_bet_competitors: [eventData.home_team, eventData.away_team],

				sport_last_bet_selection_count: 1,
			}
		}

		case EventName.sport_bet_settled: {
			return {
				sport_last_bet_id: Math.random().toString(36).substring(7),

				sport_last_bet_amount: eventData.sport_bet_amount,

				sport_last_bet_date: Date.now(),
				sport_last_bet_odds: eventData.odds,

				sport_last_bet_timing: 'LIVE',
				sport_last_bet_type: 'SINGLE',
				sport_last_bet_virtual: false,
				sport_last_bet_sports: [eventData.sport],
				sport_last_bet_leagues: [eventData.league],
				sport_last_bet_competitors: [eventData.home_team, eventData.away_team],

				sport_last_bet_selection_count: 1,
				sport_last_bet_win_amount: eventData.sport_win_amount || 0,
				sport_last_settlement_status: eventData.sport_bet_status ? 'WON' : 'LOST',
			}
		}
		case EventName.betb_risk_update: {
			return {
				betb_average_session_time_perc_increase: eventData.betb_average_session_time_perc_increase,
				betb_frequency_of_play_perc_increase: eventData.betb_frequency_of_play_perc_increase,
				betb_exhibited_risk_level: eventData.betb_exhibited_risk_level,
				betb_frequency_risk_level: eventData.betb_frequency_risk_level,
				betb_overall_risk_level: eventData.betb_overall_risk_level,
				betb_predictive_risk_level: eventData.betb_predictive_risk_level,
			}
		}

		default: {
			throw new Error(`Not mapped event ${eventName}`)
		}
	}
}

interface CashierModalProps {
	initialTab?: 'deposit' | 'withdraw'
	onSubmit: (eventName?: EventName, eventData?: any, close?: boolean) => void
}

const QUICK_AMOUNTS = [50, 100, 500, 1000]
const MAX_AMOUNT = 1000

const formatEuro = (value: number, decimals = 2) =>
	value.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })

const CashierModal: React.FC<CashierModalProps> = ({ initialTab = 'deposit', onSubmit }) => {
	const PAYMENT_OPTIONS = [
		{ type: 'Master Card', label: 'Mastercard', img: <MastercardMark /> },
		{ type: 'Visa', label: 'VISA', img: <VisaMark /> },
		{ type: 'Via Bank', label: 'Via Bank', img: <BankIcon size={30} /> },
		{ type: 'Wise', label: 'Wise', img: <WiseMark />, strong: true },
	]

	const dispatch = useDispatch()
	const navigate = useNavigate()
	const isMobile = Utils.isMobile()
	const wallet = useAppSelector((state: RootState) => state.wallet)

	const [activeTab, setActiveTab] = useState<'deposit' | 'withdraw'>(initialTab)
	const [activeOption, setActiveOption] = useState('Master Card')
	const [eventData, setEventData] = useState<EventData>(eventInitialData)
	const [amountText, setAmountText] = useState(formatEuro(eventInitialData.amount))
	const [offersOpen, setOffersOpen] = useState(false)
	const [limitsOpen, setLimitsOpen] = useState(false)
	const [searchParams, setSearchParams] = useSearchParams()
	const [withdrawError, setWithdrawError] = useState('')

	useEffect(() => {
		const type = searchParams.get('type')
		if (isMobile && type === 'withdraw') {
			setActiveTab('withdraw')
			setSearchParams({})
			return
		}

		setActiveTab(initialTab)
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialTab])

	useEffect(() => {
		if (activeTab === 'deposit' && Smartico.lib()?.showBanner) {
			Smartico.lib().showBanner({ placement_key: 'cashier-banner-placement', dom_id: 'deposit_promo' })
		}
	}, [activeTab])

	const sendEvent = (eventName: EventName, payload: any = {}, close: boolean = true) => {
		const data = cleanEvent(eventName, { ...eventData, ...payload, brand_key: BRAND_KEY })
		onSubmit(eventName, data, close)
		dispatch(closeModal())
	}

	const withdraw = async () => {
		setWithdrawError('')
		if (!(eventData.amount > 0)) {
			setWithdrawError('Enter an amount to withdraw')
			return
		}
		if (eventData.amount > wallet.balance) {
			setWithdrawError('Insufficient balance')
			return
		}
		const success = await API.withdraw(eventData.amount)
		if (!success) {
			setWithdrawError('Withdrawal failed, please try again')
			return
		}
		dispatch(closeModal())
	}

	const setPropValue = (prop: string, value: any) => {
		setWithdrawError('')
		const e: EventData = {
			...eventData,
			[prop]: value,
		}

		setEventData(e)
	}

	const setAmount = (value: number) => {
		setPropValue('amount', value)
		setAmountText(formatEuro(value))
	}

	const handleAmountChange = (raw: string) => {
		const cleaned = raw.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1')
		const parsed = parseFloat(cleaned) || 0
		if (parsed > MAX_AMOUNT) {
			setAmount(MAX_AMOUNT)
			return
		}
		setAmountText(cleaned)
		setPropValue('amount', parsed)
	}

	const handleClose = () => {
		if (isMobile) {
			navigate(-1)
		} else {
			dispatch(closeModal())
		}
	}

	const isDeposit = activeTab === 'deposit'
	const amountLabel = `€${formatEuro(eventData.amount || 0)}`

	return (
		<div className={`deposit-withdraw-modal ${ModalCssAnimation}`}>
			<div className="cashier-title">
				<WalletIcon className="cashier-title-icon" size={30} />
				<h2>Cashier</h2>
				<button type="button" className="close-btn" aria-label="Close" onClick={handleClose}>
					<CloseIcon size={24} />
				</button>
			</div>
			<div className="modal-header" role="tablist">
				<button type="button" role="tab" aria-selected={isDeposit} className={`tab ${isDeposit ? 'active' : ''}`} onClick={() => setActiveTab('deposit')}>
					Deposit
				</button>
				<button type="button" role="tab" aria-selected={!isDeposit} className={`tab ${!isDeposit ? 'active' : ''}`} onClick={() => setActiveTab('withdraw')}>
					Withdraw
				</button>
			</div>
			<div className="modal-content">
				<div className="current-balance">
					<div className="balance-label">Current balance</div>
					<div className="balance-value">€ {formatEuro(wallet.balance)}</div>
				</div>

				<div className="cashier-section">
					<label className="section-label" htmlFor="cashier-amount">
						{isDeposit ? 'Deposit amount' : 'Withdraw amount'}
					</label>
					<div className="currency-input">
						<span className="currency-symbol">€</span>
						<input
							id="cashier-amount"
							type="text"
							inputMode="decimal"
							placeholder="0.00"
							value={amountText}
							onChange={(event) => handleAmountChange(event.target.value)}
							onBlur={() => setAmountText(formatEuro(eventData.amount || 0))}
						/>
						<span className="currency-code">EUR</span>
					</div>
					{!isDeposit && withdrawError && <div className="withdraw-error">{withdrawError}</div>}
					<div className="amount-options">
						{QUICK_AMOUNTS.map((value) => {
							const selected = eventData.amount === value
							return (
								<button type="button" key={value} className={`amount-chip ${selected ? 'selected' : ''}`} onClick={() => setAmount(value)}>
									€{formatEuro(value, 0)}
									{selected && <CheckCircleIcon className="chip-check" size={20} tick="#7b68ee" />}
								</button>
							)
						})}
					</div>
				</div>

				<div className="cashier-section">
					<div className="section-label">{isDeposit ? 'Payment method' : 'Withdraw to'}</div>
					<div className="payment-options" role="radiogroup">
						{PAYMENT_OPTIONS.map((option) => {
							const selected = option.type === activeOption
							return (
								<button
									type="button"
									role="radio"
									aria-checked={selected}
									key={option.type}
									className={`payment-option ${selected ? 'active' : ''}`}
									onClick={() => setActiveOption(option.type)}
								>
									<span className="payment-logo">{option.img}</span>
									<span className={`payment-label ${option.strong ? 'strong' : ''}`}>{option.label}</span>
									{selected ? <CheckCircleIcon className="payment-radio checked" size={24} tick="#6c5ad9" /> : <span className="payment-radio" />}
								</button>
							)
						})}
					</div>
				</div>

				{isDeposit && (
					<div className={`offers ${offersOpen ? 'open' : ''}`}>
						<button type="button" className="offers-toggle" aria-expanded={offersOpen} onClick={() => setOffersOpen(!offersOpen)}>
							<GiftIcon size={24} />
							<span>Offers &amp; terms</span>
							<ChevronDownIcon className="offers-chevron" size={22} />
						</button>
						{offersOpen && (
							<div className="offers-body">
								<p>
									<strong>+180% bonus</strong> on deposits of €500 or more.
								</p>
								<p>Bonus funds carry a 35× wagering requirement and expire after 30 days.</p>
							</div>
						)}
					</div>
				)}
			</div>
			<button
				type="button"
				className={`action-button ${isDeposit ? 'deposit' : 'withdraw'}`}
				onClick={() => (isDeposit ? sendEvent(EventName.acc_deposit_approved) : withdraw())}
			>
				<span>
					{isDeposit ? 'Deposit' : 'Withdraw'} {amountLabel}
				</span>
				<ArrowRightIcon className="action-arrow" size={24} />
			</button>
			<button type="button" className="limits-link" onClick={() => setLimitsOpen(!limitsOpen)}>
				{isDeposit ? 'Deposit limits' : 'Withdrawal limits'}
			</button>
			{limitsOpen && <div className="limits-note">Up to €{formatEuro(MAX_AMOUNT, 0)} per transaction. You can change your limits in your profile.</div>}
			{isDeposit && <div id="deposit_promo" className="cashier-banner" />}
		</div>
	)
}

export default CashierModal
